import React, { useState, useEffect } from 'react';
import { 
  Package, 
  X, 
  Pencil, 
  Trash2, 
  AlertCircle,
  Calendar,
  CalendarClock,
  AlertTriangle,
  FileText,
  DollarSign,
  Tag,
  TrendingUp,
  CheckCircle,
  XCircle,
  Plus,
  Store,
  Barcode,
  FlaskConical,
  MapPin,
  ShieldAlert,
  Layers,
  Boxes,
  Building2,
  Pill,
  Clock
} from 'lucide-react';
import { ProductsService, type Product, type Lote } from '../../services/products/products.service';
import { SuppliersService, type Proveedor } from '../../services/suppliers/suppliers.service';
import { AuditoriaService, type AuditoriaRecord } from '../../services/auditoria/auditoria.service';
import { getFirstProductImage } from '../../utils/product-images';
import { capitalizeProductName } from '../../utils/capitalize';
import { ConfirmModal } from '../ui/ConfirmModal';
import { NotificationToast } from '../ui/NotificationToast';
import { usePermissions } from '../../hooks/usePermissions';

interface ProductDetailsDrawerProps {
  isOpen: boolean;
  product: Product | null;
  onClose: () => void;
  onEdit: (product: Product) => void;
  onAddLote?: (product: Product) => void;
  onDeleteSuccess?: () => void;
  onBaja?: (product: Product, preselectedLoteId?: string) => void;
  onReactivarSuccess?: () => void;
}

export const ProductDetailsDrawer: React.FC<ProductDetailsDrawerProps> = ({
  isOpen,
  product,
  onClose,
  onEdit,
  onAddLote,
  onDeleteSuccess,
  onBaja,
  onReactivarSuccess
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isReactivating, setIsReactivating] = useState(false);
  const [showNotification, setShowNotification] = useState(false);
  const [notificationConfig, setNotificationConfig] = useState<{
    title: string;
    message: string;
    type: 'success' | 'error' | 'warning' | 'info';
  } | null>(null);
  const [lotes, setLotes] = useState<Lote[]>([]);
  const [loadingLotes, setLoadingLotes] = useState(false);
  const [editingLoteId, setEditingLoteId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState({ numeroLote: '', cantidad: 1, cantidadInicial: 1, fechaVencimiento: '', proveedorId: '', numeroFactura: '', costoUnitario: '', presentacionId: '' });
  const [suppliers, setSuppliers] = useState<Proveedor[]>([]);
  const [savingLote, setSavingLote] = useState(false);
  const [activeTab, setActiveTab] = useState<'info' | 'lotes'>('info');
  const [mostrarLotesVacios, setMostrarLotesVacios] = useState(false);
  const [lastModification, setLastModification] = useState<AuditoriaRecord | null>(null);
  const [loadingLastModification, setLoadingLastModification] = useState(false);
  const { hasPermission, isVendedor, isSuperAdmin } = usePermissions();

  useEffect(() => {
    if (isOpen) {
      const timer = setTimeout(() => setIsVisible(true), 10);
      return () => clearTimeout(timer);
    } else {
      setIsVisible(false);
      setLotes([]);
    }
  }, [isOpen]);

  useEffect(() => {
    if (product?.id && isOpen) {
      setLoadingLotes(true);
      ProductsService.getLotesByProduct(product.id)
        .then(res => {
          if (res.success) setLotes(res.data);
        })
        .finally(() => setLoadingLotes(false));
      SuppliersService.getAllForSelect().then(res => {
        if (res.success) setSuppliers(res.data);
      });
      setLoadingLastModification(true);
      AuditoriaService.obtenerUltimaModificacion(product.id, 'Producto')
        .then(res => {
          if (res.success) setLastModification(res.data);
        })
        .catch(() => setLastModification(null))
        .finally(() => setLoadingLastModification(false));
    }
  }, [product?.id, isOpen]);

  const handleClose = () => {
    setIsVisible(false);
    setTimeout(() => onClose(), 300);
  };

  const handleEdit = () => {
    if (product) {
      handleClose();
      setTimeout(() => onEdit(product), 300);
    }
  };

  const handleDelete = () => {
    setShowDeleteConfirm(true);
  };

  const handleReactivar = async () => {
    if (!product) return;
    setIsReactivating(true);
    try {
      await ProductsService.reactivar(product.id);
      setNotificationConfig({
        title: 'Producto reactivado',
        message: `${product.nombre} ha sido reactivado correctamente.`,
        type: 'success'
      });
      setShowNotification(true);
      handleClose();
      setTimeout(() => onReactivarSuccess?.(), 300);
    } catch (error: any) {
      setNotificationConfig({
        title: 'Error',
        message: error?.response?.data?.message || 'No se pudo reactivar el producto.',
        type: 'error'
      });
      setShowNotification(true);
    } finally {
      setIsReactivating(false);
    }
  };

  const confirmDelete = async () => {
    if (!product) return;
    
    setIsDeleting(true);
    try {
      await ProductsService.delete(product.id);
      setNotificationConfig({
        title: 'Producto eliminado',
        message: `${product.nombre} ha sido eliminado correctamente.`,
        type: 'success'
      });
      setShowNotification(true);
      handleClose();
      setTimeout(() => onDeleteSuccess?.(), 300);
    } catch (error: any) {
      setNotificationConfig({
        title: 'Error',
        message: error.message || 'No se pudo eliminar el producto',
        type: 'error'
      });
      setShowNotification(true);
    } finally {
      setIsDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  const handleStartEditLote = (lote: Lote) => {
    const pres = product?.presentaciones?.find(p => p.id === lote.presentacionId);
    const factor = pres?.factor ?? 1;
    // Si el lote no tiene presentacionId, defaultear a la principal del producto
    const defaultPresentacionId = lote.presentacionId
      || product?.presentaciones?.find(p => p.esPrincipal)?.id
      || product?.presentaciones?.[0]?.id
      || '';
    setEditingLoteId(lote.id);
    setEditForm({
      numeroLote: lote.numeroLote,
      cantidad: Math.floor(lote.cantidad / factor),
      cantidadInicial: lote.cantidadInicial ? Math.floor(lote.cantidadInicial / factor) : Math.floor(lote.cantidad / factor),
      fechaVencimiento: lote.fechaVencimiento ? lote.fechaVencimiento.split('T')[0] : '',
      proveedorId: lote.proveedorId || '',
      numeroFactura: lote.numeroFactura || '',
      costoUnitario: lote.costoUnitario ? String(Number(lote.costoUnitario) * factor) : '',
      presentacionId: defaultPresentacionId,
    });
  };

  const handleSaveLote = async (loteId: string) => {
    setSavingLote(true);
    try {
      const proveedorNombre = editForm.proveedorId
        ? suppliers.find(s => s.id === editForm.proveedorId)?.nombre
        : undefined;
      const res = await ProductsService.updateLote(loteId, {
        numeroLote: editForm.numeroLote,
        cantidad: editForm.cantidad,
        cantidadInicial: isSuperAdmin() ? editForm.cantidadInicial : undefined,
        costoUnitario: editForm.costoUnitario ? Number(editForm.costoUnitario) : undefined,
        fechaVencimiento: editForm.fechaVencimiento || undefined,
        proveedor: proveedorNombre,
        proveedorId: editForm.proveedorId || undefined,
        numeroFactura: editForm.numeroFactura || undefined,
        presentacionId: editForm.presentacionId || undefined,
      });
      if (res.success) {
        setLotes(prev => prev.map(l => l.id === loteId ? res.data : l));
        setEditingLoteId(null);
        setNotificationConfig({ title: 'Lote actualizado', message: 'La información del lote se guardó correctamente.', type: 'success' });
        setShowNotification(true);
      } else {
        setNotificationConfig({ title: 'Error', message: res.message || 'No se pudo actualizar', type: 'error' });
        setShowNotification(true);
      }
    } catch (error: any) {
      setNotificationConfig({ title: 'Error', message: error.message || 'Error al guardar', type: 'error' });
      setShowNotification(true);
    } finally {
      setSavingLote(false);
    }
  };

  const handleDeleteLote = async (loteId: string) => {
    if (!confirm('¿Eliminar este lote? El stock del producto se ajustará automáticamente.')) return;
    try {
      const res = await ProductsService.deleteLote(loteId);
      if (res.success) {
        setLotes(prev => prev.filter(l => l.id !== loteId));
        setNotificationConfig({ title: 'Lote eliminado', message: 'El lote fue eliminado y el stock ajustado.', type: 'success' });
        setShowNotification(true);
      } else {
        setNotificationConfig({ title: 'Error', message: res.message || 'No se pudo eliminar', type: 'error' });
        setShowNotification(true);
      }
    } catch (error: any) {
      setNotificationConfig({ title: 'Error', message: error.message || 'Error al eliminar', type: 'error' });
      setShowNotification(true);
    }
  };

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency: 'COP',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(price);
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'No registrada';
    return new Date(dateString).toLocaleDateString('es-CO', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getStockStatus = (stock: number) => {
    if (stock === 0) return { label: 'Agotado', color: 'text-red-600', bg: 'bg-red-50', border: 'border-red-200', icon: XCircle };
    if (stock <= 5) return { label: 'Bajo stock', color: 'text-amber-600', bg: 'bg-amber-50', border: 'border-amber-200', icon: AlertCircle };
    return { label: 'Disponible', color: 'text-emerald-600', bg: 'bg-emerald-50', border: 'border-emerald-200', icon: CheckCircle };
  };

  const getVencimientoInfo = (lotesArr: Lote[]) => {
    if (!lotesArr || lotesArr.length === 0) return null;
    const conFecha = lotesArr.filter(l => l.fechaVencimiento);
    if (conFecha.length === 0) return null;
    const hoy = new Date();
    const fechas = conFecha.map(l => ({
      lote: l,
      dias: Math.ceil((new Date(l.fechaVencimiento!).getTime() - hoy.getTime()) / (1000 * 60 * 60 * 24))
    })).sort((a, b) => a.dias - b.dias);
    const masProximo = fechas[0];
    const vencidos = fechas.filter(f => f.dias < 0).length;
    const porVencer = fechas.filter(f => f.dias >= 0 && f.dias <= 30).length;
    return { masProximo, vencidos, porVencer, total: conFecha.length };
  };

  const getVencimientoBadge = (dias: number) => {
    if (dias < 0) return { label: `Vencido (${Math.abs(dias)}d)`, bg: 'bg-red-100', text: 'text-red-800', border: 'border-red-300', icon: AlertTriangle, urgent: true };
    if (dias === 0) return { label: 'Vence hoy', bg: 'bg-red-100', text: 'text-red-800', border: 'border-red-300', icon: AlertTriangle, urgent: true };
    if (dias <= 7) return { label: `${dias} días`, bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-200', icon: AlertTriangle, urgent: true };
    if (dias <= 30) return { label: `${dias} días`, bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200', icon: CalendarClock, urgent: true };
    if (dias <= 90) return { label: `${dias} días`, bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-200', icon: Calendar, urgent: false };
    return { label: `${dias} días`, bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200', icon: Calendar, urgent: false };
  };

  if (!isOpen || !product) return null;

  const principal = product.presentaciones?.find(p => p.esPrincipal) || product.presentaciones?.[0];
  const stockTotal = product.stockBase ?? 0;

  return (
    <>
      {/* Overlay */}
      <div 
        className={`fixed inset-0 z-40 transition-opacity duration-300 ${
          isVisible ? 'opacity-100' : 'opacity-0'
        }`}
        onClick={handleClose}
      />
      
      {/* Drawer */}
      <div 
        className={`fixed right-0 top-20 h-[calc(100vh-5rem)] w-full max-w-5xl bg-gray-50 shadow-2xl z-40 transform transition-transform duration-300 ease-in-out ${
          isVisible ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        <div className="h-full flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-white">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-emerald-100 rounded-lg">
                <Package className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">
                  Ficha del Producto
                </h2>
                <p className="text-sm text-gray-500">
                  Información completa del inventario
                </p>
              </div>
            </div>
            <button
              onClick={handleClose}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6 space-y-6">
            {/* ===== HERO SECTION ===== */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
              <div className="flex gap-6">
                {/* Imagen */}
                <div className="flex-shrink-0">
                  {getFirstProductImage(product.imagenes) ? (
                    <img 
                      src={getFirstProductImage(product.imagenes)} 
                      alt={product.nombre} 
                      className="w-44 h-44 rounded-2xl object-cover shadow-md border border-gray-100"
                    />
                  ) : (
                    <div className="w-44 h-44 rounded-2xl bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center shadow-md border border-gray-100">
                      <Package className="w-16 h-16 text-gray-400" />
                    </div>
                  )}
                </div>

                {/* Info principal */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0">
                      <h3 className="text-2xl font-bold text-gray-900 leading-tight">
                        {capitalizeProductName(product.nombre)}
                      </h3>
                      {product.variante && (
                        <p className="text-lg text-emerald-600 font-medium mt-1">
                          {product.variante}
                        </p>
                      )}
                    </div>
                    <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200 flex-shrink-0">
                      <Tag className="w-3.5 h-3.5" />
                      {product.categoria}
                    </span>
                  </div>

                  {/* Código de barras y ubicación */}
                  <div className="flex flex-wrap gap-4 mt-4">
                    {product.codigoBarras && (
                      <div className="flex items-center gap-2 px-3 py-2 bg-gray-50 rounded-lg border border-gray-100">
                        <Barcode className="w-4 h-4 text-gray-500" />
                        <span className="text-sm font-mono font-medium text-gray-700">{product.codigoBarras}</span>
                      </div>
                    )}
                    {product.ubicacion && (
                      <div className="flex items-center gap-2 px-3 py-2 bg-gray-50 rounded-lg border border-gray-100">
                        <MapPin className="w-4 h-4 text-gray-500" />
                        <span className="text-sm text-gray-700">{product.ubicacion}</span>
                      </div>
                    )}
                  </div>

                  {/* Indicador receta */}
                  <div className={`flex items-center gap-3 p-3 mt-4 rounded-xl border ${product.requiereReceta ? 'bg-red-50 border-red-200' : 'bg-emerald-50 border-emerald-200'}`}>
                    {product.requiereReceta ? (
                      <ShieldAlert className="w-5 h-5 text-red-500 flex-shrink-0" />
                    ) : (
                      <CheckCircle className="w-5 h-5 text-emerald-500 flex-shrink-0" />
                    )}
                    <div>
                      <p className={`text-sm font-bold ${product.requiereReceta ? 'text-red-700' : 'text-emerald-700'}`}>
                        {product.requiereReceta ? 'Requiere receta médica' : 'No requiere receta médica'}
                      </p>
                      <p className={`text-xs ${product.requiereReceta ? 'text-red-600' : 'text-emerald-600'}`}>
                        {product.requiereReceta
                          ? 'Este producto necesita prescripción médica para su venta'
                          : 'Este producto puede venderse sin prescripción médica'}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* ===== QUICK STATS ===== */}
            {(() => {
              const vencInfo = getVencimientoInfo(lotes);
              const st = getStockStatus(stockTotal);
              return (
                <div className="grid grid-cols-4 gap-4">
                  <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-200">
                    <div className="flex items-center gap-2 mb-2">
                      <Boxes className="w-4 h-4 text-emerald-500" />
                      <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">Stock Total</span>
                    </div>
                    <p className="text-2xl font-bold text-gray-900">{stockTotal.toLocaleString()}</p>
                    <p className={`text-xs font-medium mt-1 ${st.color}`}>{st.label}</p>
                  </div>
                  <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-200">
                    <div className="flex items-center gap-2 mb-2">
                      <Layers className="w-4 h-4 text-blue-500" />
                      <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">Presentaciones</span>
                    </div>
                    <p className="text-2xl font-bold text-gray-900">{product.presentaciones?.length || 0}</p>
                    <p className="text-xs text-gray-500 mt-1">
                      Principal: {principal?.nombre || '—'}
                    </p>
                  </div>
                  <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-200">
                    <div className="flex items-center gap-2 mb-2">
                      <Calendar className="w-4 h-4 text-purple-500" />
                      <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">Lotes</span>
                    </div>
                    <p className="text-2xl font-bold text-gray-900">{lotes.length}</p>
                    <p className="text-xs text-gray-500 mt-1">
                      {vencInfo ? `${vencInfo.total} con fecha` : 'Sin fechas'}
                    </p>
                  </div>
                  <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-200">
                    <div className="flex items-center gap-2 mb-2">
                      <CalendarClock className="w-4 h-4 text-amber-500" />
                      <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">Vencimiento</span>
                    </div>
                    {vencInfo ? (
                      (() => {
                        const badge = getVencimientoBadge(vencInfo.masProximo.dias);
                        const Icon = badge.icon;
                        return (
                          <>
                            <p className={`text-lg font-bold ${badge.text}`}>{badge.label}</p>
                            <p className="text-xs text-gray-500 mt-1">Lote {vencInfo.masProximo.lote.numeroLote}</p>
                          </>
                        );
                      })()
                    ) : (
                      <p className="text-lg font-bold text-gray-400">N/A</p>
                    )}
                  </div>
                </div>
              );
            })()}

            {/* ===== TABS ===== */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              <div className="flex border-b border-gray-200">
                <button
                  onClick={() => setActiveTab('info')}
                  className={`flex-1 pb-3 pt-4 text-sm font-semibold text-center transition-colors ${
                    activeTab === 'info'
                      ? 'text-emerald-600 border-b-2 border-emerald-600'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  Información General
                </button>
                <button
                  onClick={() => setActiveTab('lotes')}
                  className={`flex-1 pb-3 pt-4 text-sm font-semibold text-center transition-colors ${
                    activeTab === 'lotes'
                      ? 'text-emerald-600 border-b-2 border-emerald-600'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  Historial de Ingresos ({lotes.filter(l => l.cantidad > 0).length})
                </button>
              </div>

              <div className="p-6">
                {activeTab === 'info' ? (
                  <div className="space-y-6">
                    {/* Presentaciones como tarjetas */}
                    <div>
                      <h4 className="text-sm font-bold text-gray-900 uppercase tracking-wider mb-4 flex items-center gap-2">
                        <Layers className="w-4 h-4 text-emerald-500" />
                        Presentaciones y Precios
                      </h4>
                      <div className="grid grid-cols-3 gap-4">
                        {product.presentaciones?.map(p => {
                          const stockReal = p.stock ?? Math.floor((product.stockBase ?? 0) / (p.factor || 1));
                          const st = getStockStatus(stockReal);
                          const Icon = st.icon;
                          const porcentajeStock = Math.min(100, (stockReal / 20) * 100); // Normalizado visual
                          return (
                            <div key={p.id} className={`relative p-5 rounded-xl border-2 ${p.esPrincipal ? 'border-emerald-300 bg-emerald-50/50' : 'border-gray-200 bg-white'}`}>
                              {p.esPrincipal && (
                                <span className="absolute top-3 right-3 text-[10px] font-bold bg-emerald-600 text-white px-2 py-0.5 rounded-full uppercase tracking-wider">
                                  Principal
                                </span>
                              )}
                              <div className="flex items-center gap-2 mb-3">
                                <Pill className="w-5 h-5 text-gray-500" />
                                <span className="font-bold text-gray-900 text-lg">{p.nombre}</span>
                              </div>
                              <p className="text-3xl font-black text-emerald-600 mb-1">{formatPrice(p.precio)}</p>
                              <p className="text-xs text-gray-500 mb-4">Factor: {p.factor} unidad{p.factor > 1 ? 'es' : ''}</p>
                              
                              {/* Barra de stock */}
                              <div className="mb-2">
                                <div className="flex items-center justify-between text-xs mb-1">
                                  <span className="text-gray-600 font-medium">Stock disponible</span>
                                  <span className={`font-bold ${st.color}`}>{stockReal.toLocaleString()}</span>
                                </div>
                                <div className="h-2.5 bg-gray-200 rounded-full overflow-hidden">
                                  <div className={`h-full rounded-full transition-all ${
                                    stockReal === 0 ? 'bg-red-500' : stockReal <= 5 ? 'bg-amber-500' : 'bg-emerald-500'
                                  }`} style={{ width: `${porcentajeStock}%` }} />
                                </div>
                              </div>
                              <div className={`inline-flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-bold ${st.bg} ${st.color} border ${st.border}`}>
                                <Icon className="w-3.5 h-3.5" /> {st.label}
                              </div>

                              {!isVendedor() && p.precioCompra ? (
                                <div className="mt-4 pt-3 border-t border-gray-200 space-y-1">
                                  <div className="flex justify-between text-xs">
                                    <span className="text-gray-500">Costo:</span>
                                    <span className="font-medium text-gray-700">{formatPrice(p.precioCompra)}</span>
                                  </div>
                                  <div className="flex justify-between text-xs">
                                    <span className="text-gray-500">Margen:</span>
                                    <span className={`font-bold ${((p.precio - p.precioCompra) / p.precioCompra * 100) >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                                      {((p.precio - p.precioCompra) / p.precioCompra * 100).toFixed(1)}%
                                    </span>
                                  </div>
                                  <div className="flex justify-between text-xs">
                                    <span className="text-gray-500">Ganancia estimada:</span>
                                    <span className="font-bold text-gray-700">{formatPrice((p.precio - p.precioCompra) * stockReal)}</span>
                                  </div>
                                </div>
                              ) : null}
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    {/* Descripción */}
                    {product.descripcion && (
                      <div className="p-5 bg-blue-50/50 rounded-xl border border-blue-100">
                        <h4 className="text-xs font-bold text-blue-800 uppercase tracking-wider mb-2">Descripción</h4>
                        <p className="text-sm text-blue-900 leading-relaxed">{product.descripcion}</p>
                      </div>
                    )}

                    {/* Información del producto - Grid */}
                    <div>
                      <h4 className="text-sm font-bold text-gray-900 uppercase tracking-wider mb-4">Información del Producto</h4>
                      <div className="grid grid-cols-2 gap-3">
                        {product.laboratorio && (
                          <div className="flex items-start gap-3 p-4 bg-white rounded-xl border border-gray-200">
                            <div className="p-2 bg-purple-50 rounded-lg">
                              <Building2 className="w-4 h-4 text-purple-500" />
                            </div>
                            <div>
                              <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Laboratorio</p>
                              <p className="text-sm font-semibold text-gray-900">{product.laboratorio}</p>
                            </div>
                          </div>
                        )}
                        {product.principioActivo && (
                          <div className="flex items-start gap-3 p-4 bg-white rounded-xl border border-gray-200">
                            <div className="p-2 bg-cyan-50 rounded-lg">
                              <FlaskConical className="w-4 h-4 text-cyan-500" />
                            </div>
                            <div>
                              <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Principio Activo</p>
                              <p className="text-sm font-semibold text-gray-900">{product.principioActivo}</p>
                            </div>
                          </div>
                        )}
                        {product.registroInvima && (
                          <div className="flex items-start gap-3 p-4 bg-white rounded-xl border border-gray-200">
                            <div className="p-2 bg-amber-50 rounded-lg">
                              <ShieldAlert className="w-4 h-4 text-amber-500" />
                            </div>
                            <div>
                              <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Registro INVIMA</p>
                              <p className="text-sm font-semibold text-gray-900">{product.registroInvima}</p>
                            </div>
                          </div>
                        )}
                        {product.variante && (
                          <div className="flex items-start gap-3 p-4 bg-white rounded-xl border border-gray-200">
                            <div className="p-2 bg-pink-50 rounded-lg">
                              <Tag className="w-4 h-4 text-pink-500" />
                            </div>
                            <div>
                              <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Variante</p>
                              <p className="text-sm font-semibold text-gray-900">{product.variante}</p>
                            </div>
                          </div>
                        )}
                        {product.codigoBarras && (
                          <div className="flex items-start gap-3 p-4 bg-white rounded-xl border border-gray-200">
                            <div className="p-2 bg-gray-100 rounded-lg">
                              <Barcode className="w-4 h-4 text-gray-600" />
                            </div>
                            <div>
                              <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Código de Barras</p>
                              <p className="text-sm font-mono font-semibold text-gray-900">{product.codigoBarras}</p>
                            </div>
                          </div>
                        )}
                        {product.ubicacion && (
                          <div className="flex items-start gap-3 p-4 bg-white rounded-xl border border-gray-200">
                            <div className="p-2 bg-indigo-50 rounded-lg">
                              <MapPin className="w-4 h-4 text-indigo-500" />
                            </div>
                            <div>
                              <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Ubicación</p>
                              <p className="text-sm font-semibold text-gray-900">{product.ubicacion}</p>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Última Modificación */}
                    <div className="p-4 bg-gray-50 rounded-xl border border-gray-200">
                      <div className="flex items-center gap-2 mb-3">
                        <Clock className="w-4 h-4 text-gray-500" />
                        <h4 className="text-sm font-bold text-gray-900">Última Modificación</h4>
                      </div>
                      {loadingLastModification ? (
                        <div className="animate-pulse h-4 bg-gray-200 rounded w-3/4" />
                      ) : lastModification ? (
                        <div className="space-y-2">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-semibold text-gray-900">
                              {lastModification.nombreUsuario || lastModification.emailUsuario || 'Usuario desconocido'}
                            </span>
                            <span className="text-xs px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 font-medium">
                              {lastModification.accion}
                            </span>
                          </div>
                          <p className="text-xs text-gray-500">
                            {new Date(lastModification.creadoEn).toLocaleString('es-CO', {
                              year: 'numeric',
                              month: 'long',
                              day: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit',
                            })}
                          </p>
                        </div>
                      ) : (
                        <p className="text-sm text-gray-500">No hay registros de modificación para este producto.</p>
                      )}
                    </div>

                    {/* Vencimientos */}
                    {(() => {
                      const vencInfo = getVencimientoInfo(lotes);
                      if (!vencInfo) return (
                        <div className="p-5 bg-gray-50 rounded-xl border border-gray-200">
                          <div className="flex items-center gap-2 mb-2">
                            <Calendar className="w-5 h-5 text-gray-400" />
                            <h4 className="text-sm font-bold text-gray-900">Vencimientos</h4>
                          </div>
                          <p className="text-sm text-gray-500">No hay lotes con fecha de vencimiento registrada.</p>
                        </div>
                      );
                      const badge = getVencimientoBadge(vencInfo.masProximo.dias);
                      const Icon = badge.icon;
                      return (
                        <div className={`p-5 rounded-xl border-2 ${badge.urgent ? badge.border + ' ' + badge.bg : 'border-gray-200 bg-white'}`}>
                          <div className="flex items-center gap-3 mb-4">
                            <div className={`p-2 rounded-lg ${badge.bg}`}>
                              <Icon className={`w-5 h-5 ${badge.text}`} />
                            </div>
                            <div>
                              <h4 className="text-sm font-bold text-gray-900">Próximo Vencimiento</h4>
                              <p className={`text-xs font-bold ${badge.text}`}>{badge.label}</p>
                            </div>
                            {(vencInfo.vencidos > 0 || vencInfo.porVencer > 0) && (
                              <span className={`ml-auto text-xs font-bold px-3 py-1 rounded-full ${vencInfo.vencidos > 0 ? 'bg-red-600 text-white' : 'bg-amber-500 text-white'}`}>
                                {vencInfo.vencidos > 0 ? `${vencInfo.vencidos} vencido${vencInfo.vencidos > 1 ? 's' : ''}` : `${vencInfo.porVencer} por vencer`}
                              </span>
                            )}
                          </div>
                          <div className="grid grid-cols-3 gap-4">
                            <div className="p-3 bg-white rounded-lg border border-gray-100">
                              <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Fecha</p>
                              <p className={`text-sm font-bold ${badge.text}`}>{formatDate(vencInfo.masProximo.lote.fechaVencimiento)}</p>
                            </div>
                            <div className="p-3 bg-white rounded-lg border border-gray-100">
                              <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Lote</p>
                              <p className="text-sm font-bold text-gray-900">{vencInfo.masProximo.lote.numeroLote}</p>
                            </div>
                            <div className="p-3 bg-white rounded-lg border border-gray-100">
                              <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Cantidad</p>
                              <p className="text-sm font-bold text-gray-900">{vencInfo.masProximo.lote.cantidad.toLocaleString()}</p>
                            </div>
                          </div>
                          <div className="flex gap-3 mt-4 pt-4 border-t border-gray-200/60">
                            <div className="flex-1 text-center p-2 bg-white rounded-lg border border-gray-100">
                              <p className="text-xl font-bold text-gray-900">{vencInfo.total}</p>
                              <p className="text-[10px] text-gray-500 uppercase font-medium">Lotes</p>
                            </div>
                            {vencInfo.vencidos > 0 && (
                              <div className="flex-1 text-center p-2 bg-red-50 rounded-lg border border-red-100">
                                <p className="text-xl font-bold text-red-600">{vencInfo.vencidos}</p>
                                <p className="text-[10px] text-red-500 uppercase font-medium">Vencidos</p>
                              </div>
                            )}
                            {vencInfo.porVencer > 0 && (
                              <div className="flex-1 text-center p-2 bg-amber-50 rounded-lg border border-amber-100">
                                <p className="text-xl font-bold text-amber-600">{vencInfo.porVencer}</p>
                                <p className="text-[10px] text-amber-500 uppercase font-medium">Por vencer</p>
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                ) : (
                  <div>
                    <div className="flex items-center justify-between mb-4">
                      <h4 className="text-sm font-bold text-gray-900 uppercase tracking-wider flex items-center gap-2">
                        <Calendar className="w-4 h-4 text-emerald-500" />
                        Historial de Ingresos
                      </h4>
                      <button
                        onClick={() => setMostrarLotesVacios(!mostrarLotesVacios)}
                        className={`text-xs font-medium px-2.5 py-1 rounded-full border transition-colors ${
                          mostrarLotesVacios
                            ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                            : 'bg-gray-50 text-gray-600 border-gray-200 hover:bg-gray-100'
                        }`}
                      >
                        {mostrarLotesVacios ? 'Ocultar vacíos' : 'Ver todos'}
                      </button>
                    </div>
                    {loadingLotes ? (
                      <div className="flex items-center justify-center py-8">
                        <div className="animate-spin rounded-full h-6 w-6 border-2 border-emerald-600 border-t-transparent" />
                      </div>
                    ) : lotes.length === 0 ? (
                      <div className="p-8 bg-gray-50 rounded-xl border border-gray-200 text-center">
                        <Package className="w-10 h-10 text-gray-300 mx-auto mb-2" />
                        <p className="text-sm text-gray-500 font-medium">No hay ingresos registrados</p>
                        <p className="text-xs text-gray-400 mt-1">Los lotes aparecerán aquí cuando registres compras</p>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {lotes
                          .filter((lote) => mostrarLotesVacios || lote.cantidad > 0)
                          .map((lote) => {
                          const pres = product.presentaciones?.find(p => p.id === lote.presentacionId);
                          // lote.cantidad ahora está en unidades base
                          const cantidadPresentacion = pres ? Math.floor(lote.cantidad / pres.factor) : lote.cantidad;
                          const unidadesBase = lote.cantidad;
                          const costoPresentacion = pres && lote.costoUnitario ? lote.costoUnitario * pres.factor : lote.costoUnitario;
                          const isEditing = editingLoteId === lote.id;
                          return (
                            <div key={lote.id} className="bg-white rounded-xl border border-gray-200 p-4">
                              {isEditing ? (
                                <div className="space-y-3">
                                  {(() => {
                                    const editPres = product?.presentaciones?.find(p => p.id === editForm.presentacionId);
                                    const factor = editPres?.factor ?? 1;
                                    const nuevaCantidadBase = editForm.cantidad * factor;
                                    const cantidadInicialBase = editForm.cantidadInicial * factor;
                                    const cantidadAnteriorBase = lote.cantidad;
                                    const diff = nuevaCantidadBase - cantidadAnteriorBase;
                                    const cambioGrande = Math.abs(diff) > 100 || Math.abs(diff) > cantidadAnteriorBase * 0.5;
                                    return (
                                      <>
                                        <div className="grid grid-cols-2 gap-3">
                                          <div>
                                            <label className="text-xs font-semibold text-gray-600">Número de lote</label>
                                            <input type="text" value={editForm.numeroLote} onChange={(e) => setEditForm({ ...editForm, numeroLote: e.target.value })} className="w-full px-3 py-2 rounded-lg border border-gray-300 text-sm mt-1" />
                                          </div>
                                          <div>
                                            <label className="text-xs font-semibold text-gray-600">
                                              Cantidad actual ({editPres?.nombre || 'unidades'}) *
                                            </label>
                                            <input type="number" min={0} value={editForm.cantidad} disabled={!hasPermission('Productos', 'editar stock')} onChange={(e) => setEditForm({ ...editForm, cantidad: Number(e.target.value) })} className="w-full px-3 py-2 rounded-lg border border-gray-300 text-sm mt-1 disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed" />
                                            <p className="text-[10px] text-gray-500 mt-0.5">
                                              Escribe <strong>{editPres?.nombre || 'items'}</strong>, no tabletas/unidades sueltas. El sistema multiplica por factor {factor}.
                                            </p>
                                          </div>
                                          {isSuperAdmin() && (
                                            <div>
                                              <label className="text-xs font-semibold text-amber-600">
                                                Cantidad inicial ({editPres?.nombre || 'unidades'}) <span className="normal-case font-normal text-gray-400">- Solo Super Admin</span>
                                              </label>
                                              <input type="number" min={0} value={editForm.cantidadInicial} onChange={(e) => setEditForm({ ...editForm, cantidadInicial: Number(e.target.value) })} className="w-full px-3 py-2 rounded-lg border border-amber-300 text-sm mt-1 bg-amber-50" />
                                              <p className="text-[10px] text-gray-500 mt-0.5">
                                                Escribe <strong>{editPres?.nombre || 'items'}</strong> que ingresaron. Si compraste 5 blisters y cada uno trae {factor} unidades, escribe <strong>5</strong>, no {5 * factor}.
                                              </p>
                                            </div>
                                          )}
                                          <div>
                                            <label className="text-xs font-semibold text-gray-600">Costo unitario ({editPres?.nombre || 'unidad'})</label>
                                            <input type="number" min={0} step={1} value={editForm.costoUnitario} onChange={(e) => setEditForm({ ...editForm, costoUnitario: e.target.value })} className="w-full px-3 py-2 rounded-lg border border-gray-300 text-sm mt-1" />
                                          </div>
                                          <div>
                                            <label className="text-xs font-semibold text-gray-600">Vencimiento</label>
                                            <input type="date" value={editForm.fechaVencimiento} onChange={(e) => setEditForm({ ...editForm, fechaVencimiento: e.target.value })} className="w-full px-3 py-2 rounded-lg border border-gray-300 text-sm mt-1" />
                                          </div>
                                          <div>
                                            <label className="text-xs font-semibold text-gray-600">Proveedor</label>
                                            <select value={editForm.proveedorId} onChange={(e) => setEditForm({ ...editForm, proveedorId: e.target.value })} className="w-full px-3 py-2 rounded-lg border border-gray-300 text-sm mt-1">
                                              <option value="">Seleccionar...</option>
                                              {suppliers.map((s) => (<option key={s.id} value={s.id}>{s.nombre}</option>))}
                                            </select>
                                          </div>
                                          <div>
                                            <label className="text-xs font-semibold text-gray-600">Presentación *</label>
                                            <select value={editForm.presentacionId} onChange={(e) => setEditForm({ ...editForm, presentacionId: e.target.value })} className="w-full px-3 py-2 rounded-lg border border-gray-300 text-sm mt-1">
                                              <option value="">Seleccionar presentación...</option>
                                              {product?.presentaciones?.filter(p => p.activo).map((p) => (
                                                <option key={p.id} value={p.id}>{p.nombre} (factor {p.factor})</option>
                                              ))}
                                            </select>
                                            {!editForm.presentacionId && (
                                              <p className="text-[10px] text-red-500 mt-0.5 font-medium">Debes seleccionar una presentación para que el cálculo sea correcto.</p>
                                            )}
                                          </div>
                                        </div>

                                        {/* Preview de unidades base */}
                                        <div className="p-3 bg-blue-50 rounded-lg border border-blue-200 space-y-2">
                                          <p className="text-xs font-semibold text-blue-800 flex items-center gap-1">
                                            <Boxes className="w-3.5 h-3.5" />
                                            Así quedará guardado en unidades base
                                          </p>
                                          <div className="flex items-center justify-between text-sm border-b border-blue-200 pb-1">
                                            <span className="text-blue-900">Cantidad actual:</span>
                                            <span className="font-medium text-blue-900">
                                              {editForm.cantidad.toLocaleString()} {editPres?.nombre || 'unidades'} × {factor} = {nuevaCantidadBase.toLocaleString()} u.b.
                                            </span>
                                          </div>
                                          <div className="flex items-center justify-between text-sm">
                                            <span className="text-blue-900">Cantidad inicial:</span>
                                            <span className="font-medium text-blue-900">
                                              {editForm.cantidadInicial.toLocaleString()} {editPres?.nombre || 'unidades'} × {factor} = {cantidadInicialBase.toLocaleString()} u.b.
                                            </span>
                                          </div>
                                          <div className="text-xs text-blue-700 border-t border-blue-200 pt-1">
                                            Valor anterior del lote: {cantidadAnteriorBase.toLocaleString()} u.b.
                                          </div>
                                        </div>

                                        {/* Advertencia si hay cambio brusco */}
                                        {cambioGrande && (
                                          <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
                                            <AlertTriangle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
                                            <div>
                                              <p className="text-xs font-bold text-red-800">Cambio muy grande detectado</p>
                                              <p className="text-xs text-red-700">
                                                Estás cambiando el stock de {cantidadAnteriorBase.toLocaleString()} a {nuevaCantidadBase.toLocaleString()} unidades base
                                                ({diff > 0 ? '+' : ''}{diff.toLocaleString()}). Si solo quieres corregir un error de tipeo, verifica que estés escribiendo la cantidad en <strong>{editPres?.nombre || 'unidades'}</strong>, no en unidades base.
                                              </p>
                                            </div>
                                          </div>
                                        )}

                                        <div>
                                          <label className="text-xs font-semibold text-gray-600">Número de factura</label>
                                          <input type="text" value={editForm.numeroFactura} onChange={(e) => setEditForm({ ...editForm, numeroFactura: e.target.value })} className="w-full px-3 py-2 rounded-lg border border-gray-300 text-sm mt-1" />
                                        </div>
                                        <div className="flex items-center gap-2 pt-1">
                                          <button onClick={() => handleSaveLote(lote.id)} disabled={savingLote} className="px-4 py-2 bg-emerald-600 text-white rounded-lg text-sm font-medium hover:bg-emerald-700 disabled:opacity-50">
                                            {savingLote ? 'Guardando...' : 'Guardar cambios'}
                                          </button>
                                          <button onClick={() => setEditingLoteId(null)} className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50">
                                            Cancelar
                                          </button>
                                        </div>
                                      </>
                                    );
                                  })()}
                                </div>
                              ) : (
                                <div className="flex items-start justify-between gap-4">
                                  <div className="flex-1">
                                    <div className="flex items-center gap-2 mb-1">
                                      <Package className="w-4 h-4 text-emerald-600" />
                                      <span className="font-bold text-gray-900">{lote.numeroLote}</span>
                                      <span className="text-xs text-gray-400">· {formatDate(lote.fechaIngreso)}</span>
                                    </div>
                                    <div className="flex items-center gap-3 text-sm">
                                      <span className="text-gray-600">
                                        {cantidadPresentacion.toLocaleString()} × <span className="font-medium text-gray-800">{pres?.nombre || 'Unidad'}</span>
                                      </span>
                                      <span className="text-emerald-600 font-bold">+{unidadesBase.toLocaleString()} unidades base</span>
                                    </div>
                                    {costoPresentacion !== undefined && costoPresentacion !== null && (
                                      <div className="text-sm text-gray-600 mt-1">
                                        <span className="font-medium text-gray-800">Costo unitario:</span>{' '}
                                        <span className="text-emerald-700 font-semibold">{formatPrice(costoPresentacion)}</span>
                                        {pres && pres.precio > 0 && costoPresentacion > 0 && (
                                          <span className="text-xs text-gray-500 ml-2">
                                            (margen {((pres.precio - costoPresentacion) / costoPresentacion * 100).toFixed(1)}%)
                                          </span>
                                        )}
                                      </div>
                                    )}
                                    {lote.fechaVencimiento && (() => {
                                      const dias = Math.ceil((new Date(lote.fechaVencimiento).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24));
                                      const badge = getVencimientoBadge(dias);
                                      const Icon = badge.icon;
                                      return (
                                        <div className={`mt-2 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium border ${badge.bg} ${badge.text} ${badge.border}`}>
                                          <Icon className="w-3.5 h-3.5" />
                                          <span>Vence {formatDate(lote.fechaVencimiento)}</span>
                                          <span className="font-bold">({badge.label})</span>
                                        </div>
                                      );
                                    })()}
                                    {(lote.proveedorId || lote.numeroFactura) && (
                                      <div className="mt-2 flex flex-wrap gap-2">
                                        {lote.proveedorId && (
                                          <span className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs bg-blue-50 text-blue-700 border border-blue-200">
                                            <Store className="w-3 h-3" />
                                            {suppliers.find(s => s.id === lote.proveedorId)?.nombre || lote.proveedor}
                                          </span>
                                        )}
                                        {lote.numeroFactura && (
                                          <span className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs bg-purple-50 text-purple-700 border border-purple-200">
                                            <FileText className="w-3 h-3" />
                                            {lote.numeroFactura}
                                          </span>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                  <div className="flex items-center gap-1">
                                    {hasPermission('Productos', 'editar') && (
                                      <button onClick={() => handleStartEditLote(lote)} className="p-2 text-gray-400 hover:text-emerald-600 hover:bg-emerald-50 rounded-lg transition-colors" title="Editar">
                                        <Pencil className="w-4 h-4" />
                                      </button>
                                    )}
                                    {(() => {
                                      const dias = lote.fechaVencimiento ? Math.ceil((new Date(lote.fechaVencimiento).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24)) : null;
                                      const esVencido = dias !== null && dias < 0;
                                      return esVencido && hasPermission('Productos', 'editar') && onBaja ? (
                                        <button
                                          onClick={() => { onBaja(product, lote.id); handleClose(); }}
                                          className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                          title="Dar de baja lote vencido"
                                        >
                                          <Trash2 className="w-4 h-4" />
                                        </button>
                                      ) : null;
                                    })()}
                                    {hasPermission('Productos', 'eliminar') && (
                                      <button onClick={() => handleDeleteLote(lote.id)} className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors" title="Eliminar">
                                        <Trash2 className="w-4 h-4" />
                                      </button>
                                    )}
                                  </div>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t border-gray-200 bg-white">
            <div className="flex gap-3">
              {hasPermission('Productos', 'editar') && (
                <button
                  onClick={handleEdit}
                  className="flex-1 px-4 py-2.5 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors flex items-center justify-center gap-2 font-medium"
                >
                  <Pencil className="w-5 h-5" />
                  Editar Producto
                </button>
              )}
              {onAddLote && product && hasPermission('Productos', 'crear') && (
                <button
                  onClick={() => { onAddLote(product); handleClose(); }}
                  className="px-5 py-2.5 border border-emerald-300 text-emerald-700 rounded-lg hover:bg-emerald-50 transition-colors flex items-center justify-center gap-2 font-medium"
                  title="Registrar ingreso"
                >
                  <Plus className="w-5 h-5" />
                  Ingreso
                </button>
              )}
              {hasPermission('Productos', 'editar') && product && (
                product.activo ? (
                  <button
                    onClick={() => {
                      if (onBaja) {
                        handleClose();
                        setTimeout(() => onBaja(product), 300);
                      }
                    }}
                    className="px-4 py-2.5 border border-red-300 text-red-600 rounded-lg hover:bg-red-50 transition-colors flex items-center justify-center gap-2 font-medium"
                    title="Dar de baja"
                  >
                    <Trash2 className="w-5 h-5" />
                    Baja
                  </button>
                ) : (
                  <button
                    onClick={handleReactivar}
                    disabled={isReactivating}
                    className="px-4 py-2.5 border border-emerald-300 text-emerald-700 rounded-lg hover:bg-emerald-50 transition-colors flex items-center justify-center gap-2 font-medium disabled:opacity-50"
                    title="Reactivar"
                  >
                    {isReactivating ? (
                      <span className="w-5 h-5 border-2 border-emerald-600 border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <CheckCircle className="w-5 h-5" />
                    )}
                    Reactivar
                  </button>
                )
              )}
              {hasPermission('Productos', 'eliminar') && (
                <button
                  onClick={handleDelete}
                  className="px-4 py-2.5 border border-red-300 text-red-600 rounded-lg hover:bg-red-50 transition-colors flex items-center justify-center"
                  title="Eliminar"
                >
                  <Trash2 className="w-5 h-5" />
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Delete Confirmation */}
      {showDeleteConfirm && (
        <ConfirmModal
          isOpen={showDeleteConfirm}
          onClose={() => setShowDeleteConfirm(false)}
          onConfirm={confirmDelete}
          title="Eliminar Producto"
          message={`¿Estás seguro de que deseas eliminar "${product.nombre}"? Esta acción no se puede deshacer.`}
          confirmText="Eliminar"
          cancelText="Cancelar"
          type="danger"
          isLoading={isDeleting}
        />
      )}

      {/* Notification */}
      {showNotification && notificationConfig && (
        <NotificationToast
          isOpen={showNotification}
          title={notificationConfig.title}
          message={notificationConfig.message}
          type={notificationConfig.type}
          onClose={() => setShowNotification(false)}
        />
      )}
    </>
  );
};

export default ProductDetailsDrawer;
